import { getProvider } from '../svpProvider';

import { requestSVP } from './shared/requestSVP';

import type { AssetDataSource, SvpAsset } from '../../types';
import type { SvpFetchParams } from './shared/requestSVP';

const normalizeParams = (params: SvpFetchParams) => {
  const result = { ...params };
  if ('filter' in result) {
    result.filter = Object.fromEntries(
      Object.entries(params.filter as Record<string, unknown>).filter(
        ([_k, v]) => !['', undefined, null].includes(v as never)
      )
    ) as SvpFetchParams['filter'];
  }
  return result;
};

export async function fetchAssetsFromEndpoint(
  endpoint: AssetDataSource,
  params: SvpFetchParams,
  signal?: AbortSignal
) {
  if (endpoint === 'svp/assets') {
    return fetchAssets(normalizeParams(params), signal);
  }

  if (endpoint === 'svp/most-seen') {
    return fetchMostSeenAssets(normalizeParams(params), signal);
  }

  if (endpoint === 'svp/search') {
    return searchAssets(normalizeParams(params), signal);
  }
}

export async function fetchAssets(
  params: SvpFetchParams,
  signal?: AbortSignal
) {
  const filter: Record<string, string> =
    (params.filter as Record<string, string>) ?? {};
  filter.assetType = 'video';

  const json = await requestSVP<{
    _embedded: { assets: SvpAsset[] };
  }>({
    provider: getProvider(),
    path: '/assets',
    params: {
      limit: 10,
      ...normalizeParams(params),
      filter,
    },
    signal,
  });

  return json._embedded ? json._embedded.assets : [];
}

export async function fetchMostSeenAssets(
  params: SvpFetchParams,
  signal?: AbortSignal
) {
  const filter: Record<string, string> =
    (params.filter as Record<string, string>) ?? {};
  filter.assetType = 'video';

  const json = await requestSVP<{
    _embedded: { assets: SvpAsset[] };
  }>({
    provider: getProvider(),
    path: '/assets/most-seen',
    params: {
      limit: 10,
      ...params,
      filter,
    },
    signal,
  });

  return json._embedded ? json._embedded.assets : [];
}

export async function searchAssets(
  params: SvpFetchParams,
  signal?: AbortSignal
) {
  const filter: Record<string, string> =
    (params.filter as Record<string, string>) ?? {};
  filter.assetType = 'video';

  const json = await requestSVP<{
    _embedded: { assets: SvpAsset[] };
  }>({
    provider: getProvider(),
    path: '/search',
    params: {
      limit: 10,
      ...params,
      filter,
    },
    signal,
  });

  return json._embedded ? json._embedded.assets : [];
}

export async function fetchAssetById(
  assetId: number,
  params: SvpFetchParams = {}
) {
  const filter: Record<string, string> =
    (params.filter as Record<string, string>) ?? {};
  filter.assetType = 'video';

  const json = await requestSVP<SvpAsset>({
    provider: getProvider(),
    path: `/assets/${assetId}`,
    params: normalizeParams({
      ...params,
      filter,
    }),
  });

  return json;
}

export async function fetchAssetsByIds(
  assetIds: number[],
  params: SvpFetchParams = {},
  signal?: AbortSignal
) {
  const filter: Record<string, string> =
    (params.filter as Record<string, string>) ?? {};
  filter.assetType = 'video';
  filter.assetId = assetIds.join(',');

  const json = await requestSVP<{
    _embedded: { assets: SvpAsset[] };
  }>({
    provider: getProvider(),
    path: `/assets`,
    params: normalizeParams({
      ...params,
      filter,
    }),
    signal,
  });

  return json._embedded ? json._embedded.assets : [];
}
